import React from 'react';
import { Box, Card, CardContent, Typography, alpha, Chip } from '@mui/material';
import { Warning, CheckCircle, TrendingDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const DrawdownMonitor = ({ currentDD, maxDD = -10, equity, peakEquity, marketDD, marketPeak, marketCurrent, circuitBreaker, cbLevel = 0 }) => {
  const { t } = useTranslation();
  const ddPercent = currentDD;
  const maxDDPercent = maxDD;
  const ratio = Math.abs(ddPercent / maxDDPercent);
  
  const getStatus = () => {
    if (cbLevel >= 4) return { labelKey: 'risk.critical', color: '#991b1b', icon: <TrendingDown /> };
    if (cbLevel >= 3) return { labelKey: 'risk.critical', color: '#991b1b', icon: <TrendingDown /> };
    if (cbLevel >= 2) return { labelKey: 'risk.warning', color: '#ef4444', icon: <Warning /> };
    if (cbLevel >= 1) return { labelKey: 'risk.caution', color: '#f59e0b', icon: <Warning /> };
    return { labelKey: 'risk.healthy', color: '#10b981', icon: <CheckCircle /> };
  };

  const status = getStatus();
  const remaining = maxDDPercent - ddPercent;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2.5,
        border: '1.5px solid',
        borderColor: alpha(status.color, 0.25),
        background: `linear-gradient(135deg, ${alpha(status.color, 0.03)} 0%, #ffffff 100%)`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: alpha(status.color, 0.4),
          boxShadow: `0 4px 12px ${alpha(status.color, 0.15)}`,
        },
      }}
    >
      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header with Status Badge */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: 0.5,
                color: 'text.secondary',
                lineHeight: 1,
                display: 'block',
                mb: 0.5,
              }}
            >
              {t('risk.drawdownMonitor')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', lineHeight: 1.3 }}>
              {t('risk.max')}: {maxDDPercent}% | {t('risk.remaining')}: {remaining.toFixed(2)}%
              {cbLevel > 0 && circuitBreaker && ` | Cap: ${circuitBreaker[cbLevel - 1]?.cap}%`}
            </Typography>
          </Box>
          <Chip
            icon={status.icon}
            label={t(status.labelKey)}
            size="small"
            sx={{
              height: 18,
              bgcolor: alpha(status.color, 0.1),
              color: status.color,
              fontWeight: 700,
              fontSize: '0.5625rem',
              '& .MuiChip-icon': {
                color: 'inherit',
                fontSize: 12,
                marginLeft: 0.5,
              },
              '& .MuiChip-label': {
                px: 0.75,
              },
            }}
          />
        </Box>

        {/* Value Display - Prominent */}
        <Box sx={{ mb: 'auto' }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 700, 
              color: status.color,
              fontSize: '2.5rem',
              lineHeight: 1,
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              letterSpacing: '-0.02em',
            }}
          >
            {ddPercent.toFixed(2)}
            <Typography 
              component="span" 
              sx={{ 
                color: alpha(status.color, 0.6), 
                ml: 0.5, 
                fontSize: '1.25rem',
                fontWeight: 600,
              }}
            >
              %
            </Typography>
          </Typography>
        </Box>

        {/* Risk Level Bar with Circuit Breaker Levels */}
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', fontWeight: 600 }}>
              Circuit Breaker
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.625rem', fontWeight: 700, color: status.color }}>
              {cbLevel > 0 ? `L${cbLevel}` : t('risk.safe')}
            </Typography>
          </Box>
          <Box
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: alpha('#000', 0.06),
              overflow: 'hidden',
              position: 'relative',
              mb: 0.75,
            }}
          >
            {/* Circuit breaker zone segments: 0→-4% (40%), -4→-6% (20%), -6→-8% (20%), -8→-10% (20%) */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                right: 0,
                background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.25) 0%, rgba(16, 185, 129, 0.25) 40%, rgba(245, 158, 11, 0.25) 40%, rgba(245, 158, 11, 0.25) 60%, rgba(239, 68, 68, 0.25) 60%, rgba(239, 68, 68, 0.25) 80%, rgba(153, 27, 27, 0.3) 80%, rgba(153, 27, 27, 0.3) 100%)',
              }}
            />
            {/* Current DD position indicator */}
            <Box
              sx={{
                position: 'absolute',
                left: `${Math.min(ratio * 100, 100)}%`,
                top: -1,
                width: 3,
                height: 8,
                bgcolor: status.color,
                borderRadius: 1.5,
                transform: 'translateX(-50%)',
                transition: 'left 0.5s ease',
                boxShadow: `0 0 6px ${alpha(status.color, 0.5)}`,
              }}
            />
          </Box>
          
          {/* Circuit breaker threshold labels */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0 }}>
            <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#10b981', fontWeight: 600, width: '40%' }}>
              0%
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#f59e0b', fontWeight: 600, width: '20%', textAlign: 'center' }}>
              -4%
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#ef4444', fontWeight: 600, width: '20%', textAlign: 'center' }}>
              -6%
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#991b1b', fontWeight: 600, textAlign: 'right' }}>
              -10%
            </Typography>
          </Box>

          {/* Circuit breaker detail: active level */}
          {circuitBreaker && cbLevel > 0 && (
            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {circuitBreaker.map((cb, i) => (
                <Chip
                  key={i}
                  label={`${cb.label}: ${cb.threshold}% → Cap ${cb.cap}%`}
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.5rem',
                    fontWeight: cb.active ? 700 : 500,
                    bgcolor: cb.active ? alpha(status.color, 0.12) : alpha('#000', 0.04),
                    color: cb.active ? status.color : 'text.disabled',
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Equity Info - Clean Grid */}
        {equity && peakEquity && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
              <Box>
                <Typography 
                  variant="overline" 
                  sx={{ 
                    fontSize: '0.5625rem',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    color: 'text.secondary',
                    lineHeight: 1,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  {t('risk.current')}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem', 
                    fontWeight: 700, 
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                    color: 'text.primary',
                  }}
                >
                  {(equity / 1000000).toFixed(2)}
                  <Typography component="span" sx={{ fontSize: '0.625rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
                    M
                  </Typography>
                </Typography>
              </Box>
              <Box>
                <Typography 
                  variant="overline" 
                  sx={{ 
                    fontSize: '0.5625rem',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    color: 'text.secondary',
                    lineHeight: 1,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  {t('risk.peak')}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem', 
                    fontWeight: 700, 
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                    color: 'text.primary',
                  }}
                >
                  {(peakEquity / 1000000).toFixed(2)}
                  <Typography component="span" sx={{ fontSize: '0.625rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
                    M
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* VNINDEX Market Drawdown Context */}
        {marketDD != null && marketDD < -0.01 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: alpha('#000', 0.06) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontSize: '0.5625rem', fontWeight: 600, color: 'text.secondary' }}>
                VNINDEX
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.625rem', fontWeight: 700, color: '#ef4444' }}>
                {(marketDD * 100).toFixed(1)}%
              </Typography>
            </Box>
            {marketPeak > 0 && marketCurrent > 0 && (
              <Typography variant="caption" sx={{ fontSize: '0.5625rem', color: 'text.disabled', lineHeight: 1.2 }}>
                {marketCurrent?.toFixed(0)} / {marketPeak?.toFixed(0)}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>

      {/* Critical pulse effect */}
      {status.labelKey === 'risk.critical' && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: '1.5px solid',
            borderColor: status.color,
            borderRadius: 2.5,
            animation: 'pulse 2s ease-in-out infinite',
            pointerEvents: 'none',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.2 },
              '50%': { opacity: 0.6 },
            },
          }}
        />
      )}
    </Card>
  );
};

export default DrawdownMonitor;
